import {ReactComponent as Home} from 'assets/IconSvgHome.svg';
import {ReactComponent as HomeGreen} from 'assets/IconSvgHomeGreen.svg';
import {ReactComponent as ProfileGreen} from 'assets/IconSvgProfileGreen.svg';
import {ReactComponent as Profile} from 'assets/IconSvgProfile.svg';
import {ReactComponent as PaymentGreen} from 'assets/IconSvgPayGreen.svg';
import {ReactComponent as Payment} from 'assets/IconSvgPay.svg';
import {ReactComponent as HelpGreen} from 'assets/IconSvgHelpGreen.svg';
import {ReactComponent as Help} from 'assets/IconSvgHelp.svg';
import {ReactComponent as ChartGreen} from 'assets/IconSvgChartGreen.svg';
import {ReactComponent as Chart} from 'assets/IconSvgChart.svg';
import {ReactComponent as BookGreen} from 'assets/IconSvgBookGreen.svg';
import {ReactComponent as Book} from 'assets/IconSvgBook.svg';
import {ReactComponent as Logout} from 'assets/IconSvgLogout.svg';
import {ReactComponent as LogoutGreen} from 'assets/IconSvgLogoutGreen.svg';
import {ReactComponent as Ticket} from 'assets/IconSvgTicked.svg';
import {ReactComponent as TicketGreen} from 'assets/IconSvgTickedGreen.svg';
import {ReactComponent as PaloPoint} from 'assets/IconSvgPaloPoint.svg';
import {ReactComponent as PaloPointOrange} from 'assets/IconSVGPaloPointOrange.svg';
import {ReactComponent as ImgSearch} from 'assets/IconSvgImgSearch.svg';
import {ReactComponent as Heart} from 'assets/IconSvgHeart.svg';
import {ReactComponent as Camping} from 'assets/IconSvgCamping.svg';
import {ReactComponent as Calendar} from 'assets/IconSvgCalendar.svg';
import {ReactComponent as Canceled} from 'assets/IconSvgCanceled.svg';
import {ReactComponent as Check} from 'assets/IconSvgCheck.svg';
import {ReactComponent as Edit} from 'assets/IconSVGEdit.svg';
import {ReactComponent as PalowanIcon} from 'assets/IconSVGPalowan.svg';
import {ReactComponent as StatusPending} from 'assets/IconSVGStatusPending.svg';
import {ReactComponent as FilterIcon} from 'assets/FilterIconSvg.svg';
import {ReactComponent as WinnersGreen} from 'assets/IconWinGreen.svg';
import {ReactComponent as Winners} from 'assets/IconGrisWin.svg';
import {ReactComponent as Metric} from 'assets/IconSvgMetric.svg';
import {ReactComponent as MetricGreen} from 'assets/IconSvgMetricGreen.svg';
import {ReactComponent as Event} from 'assets/IconEvent.svg';
import {ReactComponent as Settings} from 'assets/IconSettings.svg';
import {ReactComponent as Envelope} from 'assets/IconSvgEnvelope.svg';
import {ReactComponent as EventAvailable} from 'assets/IconEventAvailable.svg';
import {NAME_ICONS} from 'commons/icons/IconConst';

interface Props {
  nameIcon: string;
  active?: boolean;
  size?: number;
  color?: string;
}

type SVGIcon = React.FC<React.SVGProps<SVGSVGElement>>;

interface IconVariant {
  Component: SVGIcon;
  className?: string;
  useColor?: boolean;
}

interface IconEntry {
  default: IconVariant;
  active?: IconVariant;
}

const ICON_MAP: Record<string, IconEntry> = {
  [NAME_ICONS.HOME]: {
    default: {Component: Home},
    active: {Component: HomeGreen},
  },
  [NAME_ICONS.ADMIN]: {
    default: {Component: Profile},
    active: {Component: ProfileGreen},
  },
  [NAME_ICONS.CHARTS]: {
    default: {Component: Chart},
    active: {Component: ChartGreen},
  },
  [NAME_ICONS.BOOK]: {
    default: {Component: Book},
    active: {Component: BookGreen},
  },
  [NAME_ICONS.PAY]: {
    default: {Component: Payment},
    active: {Component: PaymentGreen},
  },
  [NAME_ICONS.HELP]: {
    default: {Component: Help},
    active: {Component: HelpGreen},
  },
  [NAME_ICONS.LOGOUT]: {
    default: {Component: Logout},
    active: {Component: LogoutGreen},
  },
  [NAME_ICONS.TICKET]: {
    default: {Component: Ticket},
    active: {Component: TicketGreen},
  },
  [NAME_ICONS.PALOPOINT]: {
    default: {Component: PaloPoint},
    active: {Component: PaloPointOrange, className: 'w-7 h-7'},
  },
  [NAME_ICONS.WINNERS]: {
    default: {Component: Winners},
    active: {Component: WinnersGreen},
  },
  [NAME_ICONS.METRIC]: {
    default: {Component: Metric},
    active: {Component: MetricGreen},
  },
  [NAME_ICONS.CANCELED]: {default: {Component: Canceled}},
  [NAME_ICONS.CHECK]: {default: {Component: Check}},
  [NAME_ICONS.EDIT]: {default: {Component: Edit}},
  [NAME_ICONS.PALOWANICON]: {
    default: {Component: PalowanIcon, className: 'w-8 h-8'},
  },
  [NAME_ICONS.STATUSPENDING]: {
    default: {Component: StatusPending, className: 'w-6 h-6'},
  },
  [NAME_ICONS.FILTER]: {default: {Component: FilterIcon, className: 'w-3 h-2'}},
  [NAME_ICONS.CALENDAR]: {default: {Component: Calendar}},
  [NAME_ICONS.CAMPING]: {default: {Component: Camping}},
  [NAME_ICONS.HEART]: {default: {Component: Heart}},
  [NAME_ICONS.IMGSEARCH]: {default: {Component: ImgSearch}},
  [NAME_ICONS.EVENT]: {default: {Component: Event, useColor: true}},
  [NAME_ICONS.SETTINGS]: {default: {Component: Settings, useColor: true}},
  [NAME_ICONS.ENVELOPE]: {default: {Component: Envelope, useColor: true}},
  [NAME_ICONS.EVENT_AVAILABLE]: {
    default: {Component: EventAvailable, useColor: true},
  },
};

const getIconEntry = (nameIcon: string) => {
  return ICON_MAP[nameIcon] ?? ICON_MAP[NAME_ICONS.HOME];
};

const getIconVariant = (entry: IconEntry, active: boolean) => {
  if (active && entry.active) {
    return entry.active;
  }
  return entry.default;
};

const getIconStyle = (useColor: boolean | undefined, color: string) => {
  if (!useColor) {
    return undefined;
  }
  return {fill: color};
};

const Icons = ({
  nameIcon,
  active = false,
  size = 5,
  color = '#353349',
}: Props) => {
  const iconSize = `w-${size} h-${size - 1}`;
  const entry = getIconEntry(nameIcon);
  const variant = getIconVariant(entry, active);
  const className = variant.className ?? iconSize;
  const style = getIconStyle(variant.useColor, color);
  return <variant.Component className={className} style={style} />;
};

export default Icons;
